import * as React from 'react';

/**
 * `useEventCallback` hook creates a ref to the callback and update it as the callback changes.
 * This is useful when the callback must be bound to an external event source, e.g. the video.js `on` method.
 *
 * Without using `useEventCallback`, the callback created on initial render will be bound to the event source
 * and will not be updated when the callback changes.
 * This causes the callback to be called with stale data, similar to how `useCallback` works.
 *
 * By wrapping the callback with `useEventCallback`, the wrapping function will be bound to the event source,
 * but the actual callback will be updated when the callback changes,
 * This ensures that the callback is called with the latest data.
 *
 * @param {Function} fn callback
 * @returns {Function} wrapped callback
 */
function useEventCallback(fn: () => void) {
  const ref = React.useRef<() => void>(() => {
    throw new Error('Cannot call an event handler while rendering.');
  });

  React.useEffect(() => {
    ref.current = fn;
  }, [fn]);

  return React.useCallback<() => $TSFixMe>(() => {
    return ref.current();
  }, [ref]);
}

export default useEventCallback;
